import React, { FC } from "react";

import {
    FlexboxGrid,
    Form as RsuiteForm,
    FormInstance,
    Schema,
    Text,
} from "rsuite";
import { FormProps } from "rsuite/esm/Form/Form";
import { useTranslation } from "react-i18next";

import { CustomField } from "store/environments/tenants/custom-fields/dooap-types";

import { validateContent } from "utils/regex-helper";
import constants from "utils/constants";

import "./Form.less";

export type FormItem<T> = T & {
    isValid: boolean;
};

interface Props extends FormProps {
    formRef: React.Ref<FormInstance>;

    customField: CustomField;
}

const Form: FC<Props> = ({
    formRef,
    disabled,
    customField,
    ...props
}: Props) => {
    const { t } = useTranslation();

    type formValueType = keyof CustomField;

    const formFieldKeys: Record<string, formValueType> = {
        code: "code",
        xmlPath: "xmlPath",
        databaseColumnName: "databaseColumnName",
        uiColumnName: "uiColumnName",
    };

    const { StringType } = Schema.Types;

    const model = Schema.Model({
        xmlPath: StringType()
            .maxLength(
                constants.maxCustomFieldLimit,
                t("tenantConfig.customFieldsPage.validation.maxLength")
            )
            .addRule(
                (value) => validateContent(value, constants.xmlSelectorRegex),
                t("tenantConfig.customFieldsPage.validation.slashInRow")
            )
            .addRule(
                (value) => validateContent(value, constants.upperCaseRegex),
                t("tenantConfig.customFieldsPage.validation.letters")
            ),
        uiColumnName: StringType()
            .isRequired(t("tenantConfig.customFieldsPage.validation.required"))
            .maxLength(
                constants.maxCustomFieldLimit,
                t("tenantConfig.customFieldsPage.validation.maxLength")
            ),
    });

    const validate = (formData: FormItem<CustomField>) => {
        const result = model.check({
            xmlPath: formData.xmlPath,
            uiColumnName: formData.uiColumnName,
        });

        return {
            isValid:
                !result?.xmlPath?.hasError && !result?.uiColumnName?.hasError,
            errors: {
                xmlPath: result?.xmlPath?.errorMessage ?? "",
                uiColumnName: result?.uiColumnName?.errorMessage ?? "",
            },
        };
    };

    const handleFormChange = (formData: Record<string, any>) => {
        const field = formData as FormItem<CustomField>;

        const validation = validate(field);

        const result = {
            ...formData,
            isValid: validation.isValid,
        };

        props?.onChange?.(result);
    };

    return (
        <>
            <RsuiteForm
                {...props}
                ref={formRef}
                model={model}
                formValue={customField}
                onChange={(data) => handleFormChange(data)}
            >
                <FlexboxGrid
                    justify={"start"}
                    align={"middle"}
                    style={{ marginBottom: "1.75rem" }}
                >
                    <FlexboxGrid.Item style={{ marginRight: "0.75rem" }}>
                        <Text className={"custom_fields__form__header"}>
                            {t(
                                "tenantConfig.customFieldsPage.table.dbColumnName"
                            )}
                        </Text>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item>
                        <Text>{customField.databaseColumnName}</Text>
                    </FlexboxGrid.Item>
                </FlexboxGrid>

                <RsuiteForm.Group className={"custom_fields__form__group"}>
                    <RsuiteForm.ControlLabel
                        className={"custom_fields__form__label"}
                    >
                        {t("tenantConfig.customFieldsPage.table.XMLElement")}
                        <RsuiteForm.HelpText
                            className={"custom_fields__form__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.customFieldsPage.validation.xmlElement"
                            )}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>
                    <RsuiteForm.Control
                        className={"custom_fields__form__control"}
                        type="text"
                        readOnly={disabled}
                        name={formFieldKeys.xmlPath}
                        value={customField.xmlPath || ""}
                        placeholder={t(
                            "tenantConfig.customFieldsPage.placeholder"
                        )}
                    />
                </RsuiteForm.Group>

                <RsuiteForm.Group className={"custom_fields__form__group"}>
                    <RsuiteForm.ControlLabel
                        className={"custom_fields__form__label"}
                    >
                        {t("tenantConfig.customFieldsPage.table.UIElementName")}
                        <sup className={"custom_fields__sub"}>*</sup>
                        <RsuiteForm.HelpText
                            className={"custom_fields__form__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.customFieldsPage.validation.uiElement"
                            )}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>
                    <RsuiteForm.Control
                        className={"custom_fields__form__control"}
                        type="text"
                        readOnly={disabled}
                        name={formFieldKeys.uiColumnName}
                        value={customField.uiColumnName || ""}
                        placeholder={t(
                            "tenantConfig.customFieldsPage.placeholder"
                        )}
                    />
                </RsuiteForm.Group>

                <Text className="custom_fields_footer">
                    <sup className={"custom_fields__sub"}>*</sup>
                    {t("tenantConfig.customFieldsPage.requiredFields")}
                </Text>
            </RsuiteForm>
        </>
    );
};

export default Form;
